import { useState, useRef, useCallback } from "react";

// ─── Browser-side image compression ──────────────────────────────────────────
// Resizes to max 1200px and converts to JPEG at 82% quality before upload.
// This keeps Firebase Storage files under ~300KB and speeds up AI scanning.

function compressImage(file, maxPx = 1200, quality = 0.82) {
  return new Promise((resolve) => {
    // PDFs pass through uncompressed — can't canvas-resize them
    if (file.type === "application/pdf") {
      const reader = new FileReader();
      reader.onload = () => resolve({ base64: reader.result.split(",")[1], mimeType: file.type, blob: file });
      reader.readAsDataURL(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width > height) { height = Math.round((height * maxPx) / width); width = maxPx; }
        else                { width = Math.round((width * maxPx) / height);  height = maxPx; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ base64: reader.result.split(",")[1], mimeType: "image/jpeg", blob });
          reader.readAsDataURL(blob);
        },
        "image/jpeg", quality
      );
    };
    img.onerror = () => {
      // Fallback — read file as-is
      const reader = new FileReader();
      reader.onload = () => resolve({ base64: reader.result.split(",")[1], mimeType: file.type, blob: file });
      reader.readAsDataURL(file);
    };
    img.src = url;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Field({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", padding: "9px 11px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
    </div>
  );
}

function EyeFields({ eye, data, onChange }) {
  const update = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: "#666", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {eye === "right" ? "OD — Right eye" : "OS — Left eye"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {["sphere", "cylinder", "axis"].map(k => <Field key={k} label={k.toUpperCase()} value={data?.[k]} onChange={v => update(k, v)} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {["add", "bc", "dia"].map(k => <Field key={k} label={k.toUpperCase()} value={data?.[k]} onChange={v => update(k, v)} />)}
      </div>
    </div>
  );
}

const LENS_TYPES = ["Soft Daily", "Soft Bi-Weekly", "Soft Monthly", "Ortho-K", "RGP Hard", "Scleral", "Toric"];

const emptyRx = () => ({
  right: { sphere: "", cylinder: "", axis: "", add: "", bc: "", dia: "" },
  left:  { sphere: "", cylinder: "", axis: "", add: "", bc: "", dia: "" },
  pd: "", examDate: "", expiryDate: "", doctor: "", clinic: "", notes: "", lensType: "Ortho-K",
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function ScanScreen({ t, onSaveRx }) {
  const [compressed, setCompressed] = useState(null); // { base64, mimeType, blob, previewUrl }
  const [scanning,   setScanning]   = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [error,      setError]      = useState("");
  const [rx,         setRx]         = useState(null);
  const [saving,     setSaving]     = useState(false);
  const fileRef = useRef();

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setError(""); setRx(null);
    setCompressing(true);
    try {
      const result = await compressImage(f);
      const previewUrl = URL.createObjectURL(result.blob);
      setCompressed({ ...result, previewUrl, originalName: f.name });
    } catch {
      setError("Could not process image. Try another file.");
    }
    setCompressing(false);
  }, []);

  const handleScan = async () => {
    if (!compressed) { setError("Please choose an image first."); return; }
    setScanning(true); setError("");
    try {
      const res = await fetch("https://eyerx-scan.alanahmad81.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "scan", imageBase64: compressed.base64, mediaType: compressed.mimeType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Scan failed");
      setRx(data.parsed || emptyRx());
      if (!data.parsed) setError("Could not read all fields — please review and fill in manually.");
    } catch (e) {
      setError(e.message || "Scan failed. You can fill in the form manually.");
      setRx(emptyRx());
    }
    setScanning(false);
  };

  const handleSave = async () => {
    if (!rx?.examDate) { setError("Exam date is required."); return; }
    setSaving(true);
    setError("");
    try {
      await onSaveRx({
        ...rx,
        _compressedBlob: compressed?.blob || null,
        _mimeType: compressed?.mimeType || "image/jpeg",
        _previewUrl: compressed?.previewUrl || "",
      });
      // Success — clear form
      setRx(null);
      setCompressed(null);
      setError("");
    } catch (e) {
      console.error("Save prescription error:", e);
      setError("Save failed: " + (e.message || "Unknown error. Please try again."));
    } finally {
      // Always reset saving state so button never stays stuck
      setSaving(false);
    }
  };

  const update = (field, value) => setRx(p => ({ ...p, [field]: value }));

  const fileSizeLabel = compressed ? (() => {
    const bytes = Math.round((compressed.base64.length * 3) / 4);
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  })() : null;

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 18, color: t.text, marginBottom: 4 }}>Scan prescription</div>
      <div style={{ fontSize: 13, color: t.muted, marginBottom: 20 }}>
        Upload a photo of your slip. AI reads all fields automatically — you can edit before saving.
        Images are compressed automatically before upload.
      </div>

      {/* Upload zone */}
      <div onClick={() => !compressing && fileRef.current.click()}
        style={{ border: `2px dashed ${compressed ? t.primary : t.secondary}`, borderRadius: 20, overflow: "hidden", cursor: compressing ? "wait" : "pointer", background: t.soft, marginBottom: 14, minHeight: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {compressing ? (
          <div style={{ textAlign: "center", padding: 28 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🗜️</div>
            <div style={{ fontWeight: 600, color: t.text, fontSize: 14 }}>Compressing image…</div>
          </div>
        ) : compressed?.previewUrl ? (
          <div style={{ width: "100%", position: "relative" }}>
            <img src={compressed.previewUrl} alt="Prescription" style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, padding: "3px 8px", borderRadius: 8 }}>
              {fileSizeLabel} · tap to change
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 28 }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>📷</div>
            <div style={{ fontWeight: 600, color: t.text, fontSize: 14 }}>Tap to upload or take photo</div>
            <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>JPG, PNG, or PDF · auto-compressed for upload</div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: "none" }}
          onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
      </div>

      {/* Download button when image exists */}
      {compressed?.previewUrl && (
        <button onClick={()=>{
            const a = document.createElement("a");
            a.href = compressed.previewUrl;
            a.download = compressed.originalName || "prescription.jpg";
            a.click();
          }}
          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"8px", background:t.soft, borderRadius:10, color:t.accent, fontSize:13, fontWeight:600, border:"none", cursor:"pointer", width:"100%", marginBottom:12 }}>
          ⬇️ Download this image
        </button>
      )}

      {error && (
        <div style={{ fontSize: 13, color: "#c62828", background: "#ffebee", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button onClick={handleScan} disabled={!compressed || scanning || compressing}
          style={{ flex: 2, padding: "13px", borderRadius: 14, border: "none", background: (!compressed || scanning || compressing) ? "#ddd" : t.grad, color: (!compressed || scanning || compressing) ? "#999" : "#fff", fontWeight: 700, fontSize: 14, cursor: compressed && !scanning && !compressing ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
          {scanning ? "🤖 Scanning…" : "🤖 AI Scan"}
        </button>
        <button onClick={() => setRx(emptyRx())}
          style={{ flex: 1, padding: "13px", borderRadius: 14, border: `1.5px solid ${t.secondary}`, background: t.soft, color: t.accent, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          ✏️ Manual
        </button>
      </div>

      {scanning && (
        <div style={{ textAlign: "center", padding: "16px 0", marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: t.muted, marginBottom: 10 }}>Reading prescription details…</div>
          <div style={{ height: 4, borderRadius: 2, background: t.soft, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 2, background: t.grad, width: "60%", animation: "scanbar 1.4s ease-in-out infinite" }} />
          </div>
          <style>{`@keyframes scanbar{0%{transform:translateX(-100%)}100%{transform:translateX(260%)}}`}</style>
        </div>
      )}

      {rx && !scanning && (
        <div style={{ background: t.card, borderRadius: 18, padding: 18, border: `1px solid ${t.secondary}30`, marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, color: t.text, fontSize: 14 }}>Review & edit details</div>
              <div style={{ fontSize: 12, color: t.muted }}>All fields are editable — correct anything the AI missed.</div>
            </div>
          </div>

          <EyeFields eye="right" data={rx.right} onChange={v => setRx(p => ({ ...p, right: v }))} />
          <EyeFields eye="left"  data={rx.left}  onChange={v => setRx(p => ({ ...p, left: v }))} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <Field label="PD (mm)" value={rx.pd} onChange={v => update("pd", v)} />
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>LENS TYPE</div>
              <select value={rx.lensType || ""} onChange={e => update("lensType", e.target.value)}
                style={{ width: "100%", padding: "9px 11px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}>
                <option value="">— Select —</option>
                {LENS_TYPES.map(lt => <option key={lt} value={lt}>{lt}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <Field label="EXAM DATE"   value={rx.examDate}   onChange={v => update("examDate", v)} />
            <Field label="EXPIRY DATE" value={rx.expiryDate} onChange={v => update("expiryDate", v)} />
          </div>
          <Field label="DOCTOR"            value={rx.doctor} onChange={v => update("doctor", v)} />
          <Field label="CLINIC / HOSPITAL" value={rx.clinic} onChange={v => update("clinic", v)} />
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>NOTES</div>
            <textarea value={rx.notes || ""} onChange={e => update("notes", e.target.value)} rows={3}
              style={{ width: "100%", padding: "9px 11px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: saving ? "#ddd" : t.grad, color: saving ? "#999" : "#fff", fontWeight: 700, fontSize: 15, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Uploading & saving…" : "💾 Save prescription"}
          </button>
        </div>
      )}
    </div>
  );
}
